import { useState, useId } from 'react'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

export default function WaitlistSection({ bgMode = 'dark' }) {
  const uid     = useId()
  const inputId = `${uid}-email`
  const errId   = `${uid}-err`

  const [email, setEmail]   = useState('')
  const [state, setState]   = useState('idle') // idle | sending | done | error
  const [errMsg, setErrMsg] = useState('')

  const isDark = bgMode === 'dark'

  const submit = async () => {
    const trimmed = email.trim()
    if (!trimmed) return
    if (!EMAIL_RE.test(trimmed)) {
      setErrMsg('Please enter a valid email address.')
      setState('error')
      return
    }
    setState('sending')
    try {
      const res = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Something went wrong')
      setState('done')
    } catch (e) {
      setErrMsg(e.message)
      setState('error')
    }
  }

  const handleKey = (e) => {
    if (e.key === 'Enter') submit()
  }

  if (state === 'done') {
    return (
      /* role="status" + aria-live="polite" announces the success message
         to screen readers without interrupting ongoing speech. */
      <div
        role="status"
        aria-live="polite"
        style={{
          maxWidth: 480, margin: '0 auto',
          background: isDark
            ? 'linear-gradient(135deg, rgba(34,197,94,0.1), rgba(16,120,50,0.07))'
            : 'linear-gradient(135deg, rgba(34,197,94,0.12), rgba(16,120,50,0.06))',
          border: '1.5px solid rgba(34,197,94,0.28)',
          borderRadius: 18, padding: '20px 24px',
          display: 'flex', alignItems: 'center', gap: 14,
          animation: 'fadeUp 0.35s ease',
        }}
      >
        <div aria-hidden="true" style={{
          width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
          background: 'rgba(34,197,94,0.15)',
          border: '1.5px solid rgba(34,197,94,0.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 18,
        }}>✓</div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: isDark ? '#86efac' : '#15803d' }}>
            You're in the flow
          </div>
          <div style={{ fontSize: 12, color: isDark ? 'rgba(140,200,150,0.6)' : 'rgba(30,80,50,0.5)', marginTop: 2 }}>
            We'll reach out when there's news.
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 480, margin: '0 auto' }}>
      <div style={{
        fontSize: 11, fontWeight: 700,
        color: isDark ? 'rgba(180,215,235,0.5)' : 'rgba(30,80,50,0.5)',
        letterSpacing: '0.12em', textTransform: 'uppercase',
        textAlign: 'center', marginBottom: 12,
      }}>
        Stay in the loop
      </div>

      {/* Visually-hidden label satisfies WCAG 1.3.1 (Info and Relationships)
          and 4.1.2 (Name, Role, Value) without affecting the visual design. */}
      <label htmlFor={inputId} style={{
        position: 'absolute', width: 1, height: 1,
        padding: 0, margin: -1, overflow: 'hidden',
        clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0,
      }}>
        Your email address
      </label>

      <div style={{
        display: 'flex',
        background: isDark ? 'rgba(4,14,7,0.85)' : 'rgba(255,255,255,0.9)',
        border: `1.5px solid ${
          state === 'error'
            ? 'rgba(248,113,113,0.45)'
            : isDark ? 'rgba(34,197,94,0.18)' : 'rgba(34,197,94,0.3)'
        }`,
        borderRadius: 16, overflow: 'hidden',
        backdropFilter: 'blur(12px)',
        boxShadow: state === 'error'
          ? '0 0 0 3px rgba(248,113,113,0.08)'
          : '0 0 0 3px transparent',
        transition: 'border-color 0.2s, box-shadow 0.2s',
      }}>
        <input
          id={inputId}
          type="email"
          autoComplete="email"
          value={email}
          onChange={e => { setEmail(e.target.value); if (state === 'error') setState('idle') }}
          onKeyDown={handleKey}
          placeholder="your@email.com"
          disabled={state === 'sending'}
          /* aria-describedby wires the live error region to this input so
             screen readers announce validation failures on focus. */
          aria-describedby={state === 'error' ? errId : undefined}
          aria-invalid={state === 'error' ? 'true' : 'false'}
          style={{
            flex: 1, background: 'transparent', border: 'none', outline: 'none',
            color: isDark ? '#c8e6cb' : '#1e2922',
            fontSize: 15, padding: '15px 18px',
            fontFamily: 'inherit',
            opacity: state === 'sending' ? 0.6 : 1,
          }}
        />
        <button
          onClick={submit}
          disabled={!email.trim() || state === 'sending'}
          aria-busy={state === 'sending'}
          style={{
            background: email.trim() && state !== 'sending'
              ? 'linear-gradient(135deg,#16a34a,#15803d)'
              : isDark ? 'rgba(10,28,14,0.7)' : 'rgba(220,240,225,0.7)',
            border: 'none', padding: '0 22px',
            color: email.trim() && state !== 'sending'
              ? '#fff'
              : isDark ? 'rgba(140,200,150,0.25)' : 'rgba(30,80,50,0.3)',
            fontSize: 13, fontWeight: 700,
            cursor: email.trim() ? 'pointer' : 'default',
            fontFamily: 'inherit', minWidth: 120, minHeight: 52,
            transition: 'all 0.2s',
            WebkitTapHighlightColor: 'transparent',
            whiteSpace: 'nowrap',
          }}
        >
          {state === 'sending' ? '…' : 'Join Flow'}
        </button>
      </div>

      {/* role="status" + aria-live="polite" ensures screen readers announce
          the error without stealing focus from the input. */}
      <div
        id={errId}
        role="status"
        aria-live="polite"
        style={{
          marginTop: state === 'error' ? 8 : 0,
          minHeight: state === 'error' ? undefined : 0,
          fontSize: 12, color: '#fca5a5',
          textAlign: 'center',
          animation: state === 'error' ? 'fadeUp 0.2s ease' : 'none',
        }}
      >
        {state === 'error' ? errMsg : ''}
      </div>

      <div style={{
        fontSize: 11,
        color: isDark ? 'rgba(140,200,150,0.3)' : 'rgba(30,80,50,0.35)',
        textAlign: 'center', marginTop: 10,
      }}>
        No spam. Unsubscribe anytime.
      </div>
    </div>
  )
}
